import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { decodeSpotQuoteResponse, encodeSpotQuoteRequest } from './spot-stream-adapter.utils';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import {
    ISessionDisposable,
    ISpotQuote,
    IStreamData,
    SpotPriceRequestWithClient,
} from '../../../interfaces';
import { ISpotStreamAdapterService } from './spot-stream-adapter.interface';
import { Observable, Subject } from 'rxjs';
import { terminate } from '../../../helpers';

@injectable()
export class SpotStreamAdapterService implements ISpotStreamAdapterService, ISessionDisposable {
    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;

    private _rawSpotFeed: Observable<IStreamData<Uint8Array>>;

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    delete = (token: number): void => {
        this.communicationService.delete(token);
    };

    private rawSpotFeed(): Observable<IStreamData<Uint8Array>> {
        return Boolean(this._rawSpotFeed) ?
            this._rawSpotFeed :
            this._rawSpotFeed = this.communicationService
                .dataObservable(gateway.MessageTypes.SpotQuote)
                .pipe(
                    takeUntil(this.destroy$),
                );
    }

    priceObservable = (token: number): Observable<ISpotQuote> => {
        return this.rawSpotFeed().pipe(
            filter(data => data.communicationToken === token),
            terminate(quote => quote.terminal),
            map(quote => {
                if (Boolean(quote.error)) {
                    AlkLogger.error(`[SPOT_STREAM_ADAPTER#priceObservable] ${ ''
                    } subscription id ${ token } ends with error: ${ JSON.stringify(quote.error) }`);
                    throw quote.error;
                }
                return quote;
            }),
            map(decodeSpotQuoteResponse),
            tap(quote =>
                AlkLogger.debug(`[SPOT_STREAM_ADAPTER#priceObservable] quote ${ JSON.stringify(quote) } ${ ''
                } for subscription id: ${ token } `)),
        );
    };

    create = (request: SpotPriceRequestWithClient): number => {
        const rawRequest = encodeSpotQuoteRequest(request);
        return this.communicationService.create(rawRequest, gateway.MessageTypes.SpotQuote);
    };

    stop(): void {
        this.destroy$.next(true);
        this._rawSpotFeed = null;
    }
}
